import { Button, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { SparklesIcon } from "lucide-react";
import * as React from "react";
import type { CreateSOPFormDTO } from "@/modules/sop/domain/contracts";
import { CreateSOPFormSchema } from "@/modules/sop/domain/contracts";
import { SOPFormSections } from "./SOPFormSections";
import { sopFormSamples } from "./sopFormSamples";

interface SOPFormProps {
  initialValues?: Partial<CreateSOPFormDTO>;
  onSubmit: (values: CreateSOPFormDTO & { status: "draft" | "active" }) => void;
  submitting?: boolean;
}

export function SOPForm({
  initialValues,
  onSubmit,
  submitting = false,
}: SOPFormProps) {
  const [sampleIndex, setSampleIndex] = React.useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  const methods = RHF.useForm<CreateSOPFormDTO>({
    resolver: zodResolver(CreateSOPFormSchema),
    defaultValues: {
      title: "",
      purpose: null,
      scope: null,
      departmentId: null,
      responsiblePersonId: null,
      positionIds: [],
      steps: [],
      documents: [],
      warnings: [],
      status: "draft",
      ...initialValues,
    },
  });

  const handleAutoFill = () => {
    const sample = sopFormSamples[sampleIndex]!;
    Object.entries(sample).forEach(([key, value]) => {
      methods.setValue(key as keyof CreateSOPFormDTO, value as never);
    });
    setSampleIndex((prev) => (prev + 1) % sopFormSamples.length);
  };

  const handleSubmit = (status: "draft" | "active") => {
    const values = methods.getValues();
    onSubmit({ ...values, status });
  };

  return (
    <FormRoot<CreateSOPFormDTO> methods={methods} className="space-y-6">
      <SOPFormSections formMethods={methods} />

      <div className="sticky bottom-2 flex justify-between border-t bg-background pt-4">
        {isDev && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="mr-2 h-4 w-4" />
            Auto Fill (Sample {sampleIndex + 1}/{sopFormSamples.length})
          </Button>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={submitting}
          >
            ບັນທຶກເປັນຮ່າງ
          </Button>
          <Button
            type="button"
            onClick={() => handleSubmit("active")}
            isLoading={submitting}
          >
            ບັນທຶກແລະເຜີຍແຜ່
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
